// The registration draw desk, as this console can draw it.
//
// THE SERVICE SHUFFLES. This file reads the desk and posts the two knobs —
// how many winners, and whether they place as equals or in order. A browser
// that picked names would be a second opinion the stored seed could not replay.

import { PEOPLE_ENDPOINTS } from "../../../api/src/admin/endpoints.js";
import { authenticated, failureFor, type ApiOptions, type Failure } from "./client.js";
import type { SessionToken } from "../session/store.js";

export type DrawPlacement = "same" | "order";

export type DrawPerson = {
  readonly attendeeRef: string;
  readonly displayName: string | null;
  readonly contactEmail: string | null;
};

export type DrawWinner = DrawPerson & {
  readonly place: number | null;
};

export type DrawRecord = {
  readonly drawId: string;
  readonly drawnAt: string;
  readonly drawnBy: string | null;
  readonly winnerCount: number;
  readonly placement: DrawPlacement;
  readonly seed: string;
  readonly poolSize: number;
  readonly winners: readonly DrawWinner[];
};

export type DrawDesk = {
  readonly activationId: string;
  readonly asAt: string;
  readonly pool: {
    readonly size: number;
    readonly note: string;
    readonly people: readonly DrawPerson[];
  };
  readonly draws: readonly DrawRecord[];
};

export type DrawRead = { readonly ok: true; readonly value: DrawDesk } | ({ readonly ok: false } & Failure);

export async function readDraws(
  options: ApiOptions,
  token: SessionToken,
  activationId: string,
): Promise<DrawRead> {
  const answer = await authenticated(options, token, {
    path: `${PEOPLE_ENDPOINTS.draw}?activation_id=${encodeURIComponent(activationId)}`,
    method: "GET",
  });
  if (!answer.ok) return { ok: false, ...failureFor(answer) };
  const desk = deskFrom(answer.body, activationId);
  if (desk === null) {
    return {
      ok: false,
      kind: "unavailable",
      detail:
        "The service answered a draw desk this console could not read. Nothing is drawn from it, because a " +
        "partly-read history would look like the whole of it.",
    };
  }
  return { ok: true, value: desk };
}

export async function runDraw(
  options: ApiOptions,
  token: SessionToken,
  input: { activationId: string; winnerCount: number; placement: DrawPlacement },
): Promise<DrawRead> {
  const answer = await authenticated(options, token, {
    path: PEOPLE_ENDPOINTS.draw,
    method: "POST",
    json: {
      activation_id: input.activationId,
      winner_count: input.winnerCount,
      placement: input.placement,
    },
  });
  if (!answer.ok) return { ok: false, ...failureFor(answer) };
  const desk = deskFrom(answer.body, input.activationId);
  if (desk === null) {
    return {
      ok: false,
      kind: "unavailable",
      detail:
        "The service recorded a draw and then answered in a shape this console could not read. Open the page again.",
    };
  }
  return { ok: true, value: desk };
}

function deskFrom(body: unknown, activationId: string): DrawDesk | null {
  const row = fields(body);
  const pool = fields(row["pool"]);
  const size = whole(pool["size"]);
  const note = text(pool["note"]);
  const asAt = text(row["as_at"]);
  if (size === null || note === null || asAt === null) return null;
  if (!Array.isArray(row["draws"])) return null;
  if (!Array.isArray(pool["people"])) return null;

  const people: DrawPerson[] = [];
  for (const item of pool["people"]) {
    const person = personFrom(item);
    if (person === null) return null;
    people.push(person);
  }

  const draws: DrawRecord[] = [];
  for (const item of row["draws"]) {
    const draw = drawFrom(item);
    if (draw === null) return null;
    draws.push(draw);
  }

  return {
    activationId: text(row["activation_id"]) ?? activationId,
    asAt,
    pool: { size, note, people },
    draws,
  };
}

function personFrom(value: unknown): DrawPerson | null {
  const row = fields(value);
  const attendeeRef = text(row["attendee_ref"]);
  if (attendeeRef === null) return null;
  return {
    attendeeRef,
    displayName: text(row["display_name"]),
    contactEmail: text(row["contact_email"]),
  };
}

function drawFrom(value: unknown): DrawRecord | null {
  const row = fields(value);
  const drawId = text(row["draw_id"]);
  const drawnAt = text(row["drawn_at"]);
  const winnerCount = whole(row["winner_count"]);
  const placement = row["placement"] === "order" ? "order" : row["placement"] === "same" ? "same" : null;
  const seed = text(row["seed"]);
  const poolSize = whole(row["pool_size"]);
  if (
    drawId === null ||
    drawnAt === null ||
    winnerCount === null ||
    placement === null ||
    seed === null ||
    poolSize === null ||
    !Array.isArray(row["winners"])
  ) {
    return null;
  }

  const winners: DrawWinner[] = [];
  for (const item of row["winners"]) {
    const winner = winnerFrom(item, placement);
    if (winner === null) return null;
    winners.push(winner);
  }

  return {
    drawId,
    drawnAt,
    drawnBy: text(row["drawn_by"]),
    winnerCount,
    placement,
    seed,
    poolSize,
    winners,
  };
}

function winnerFrom(value: unknown, placement: DrawPlacement): DrawWinner | null {
  const person = personFrom(value);
  if (person === null) return null;
  const place = fields(value)["place"];
  return {
    ...person,
    place:
      placement === "order" && typeof place === "number" && Number.isInteger(place) ? place : null,
  };
}

function fields(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function text(value: unknown): string | null {
  return typeof value === "string" && value !== "" ? value : null;
}

function whole(value: unknown): number | null {
  return typeof value === "number" && Number.isInteger(value) && value >= 0 ? value : null;
}
